import type { PageSectionTheme } from '../../lib/types.ts';

/** Color tokens for the section's filled panel — the one card role every section shares. */
export interface ThemeCardClasses {
  readonly bg: string;
  readonly text: string;
  readonly muted: string;
  readonly border: string;
}

/**
 * A full "color combination" for one section background (CMS v2, "Shade").
 *
 * The v2 rule is that a section builds depth out of its OWN color: the card is a shade of
 * the section background rather than a contrasting brand panel, so a green section is green
 * on green and a yellow one yellow on yellow. Only two things cut across a combination — the
 * coral accent (`eyebrow`, and the coral CTA pill) and the hairline (`line`).
 *
 * Roles:
 * - `bg` / `heading` / `body`: the section background, its primary ink and its muted ink.
 * - `card`: the filled panel — a shade of `bg`, with ink matching the section's.
 * - `eyebrow`: the accent for eyebrows, small labels and the FAQ "+" — coral on the dark
 *   bases, the darkened `coralInk` on the light ones (full coral fails contrast at that size).
 * - `img`: the tone behind an image — the frame it shows through and the fill while it loads.
 * - `line`: the hairline that separates rows sitting directly on the section background.
 * - `button` / `outline`: which PillButton variant the section's primary and secondary CTAs
 *   use. Coral everywhere except the coral section itself, which would otherwise put coral
 *   on coral.
 */
export interface ThemeClasses {
  readonly bg: string;
  readonly body: string;
  readonly heading: string;
  readonly headingChild: string;
  readonly guitar: string;
  readonly card: ThemeCardClasses;
  readonly eyebrow: string;
  readonly img: string;
  readonly line: string;
  readonly button: 'coral' | 'ink';
  readonly outline: 'outline' | 'outlineLight';
}

// Ink, muted ink and hairline are decided by one thing only: whether the base is light or
// dark. Everything that varies per base (the background, its card shade, its image tone) is
// passed in.
//
// On the muted strengths: because muted text is the ink at reduced opacity, the two compound —
// softening mf-black away from pure black (see design-tokens.ts) pushed the original /65 and /75
// under WCAG AA on every surface. /80 and /85 put the tightest cases (orangeDeep at 5.15:1,
// greenLight at 4.80:1) back above 4.5:1 while still reading as clearly secondary. Lower these
// only against recomputed contrast, not by eye.
const LIGHT = {
  heading: 'text-mf-black',
  body: 'text-mf-black/80',
  headingChild: '[&_h2]:text-mf-black [&_h3]:text-mf-black',
  guitar: 'text-mf-green',
  line: 'border-mf-green/20',
  eyebrow: 'text-mf-coralInk',
  button: 'coral',
  outline: 'outline',
} as const;

const DARK = {
  heading: 'text-mf-yellow',
  body: 'text-mf-yellow/85',
  headingChild: '[&_h2]:text-mf-yellow [&_h3]:text-mf-yellow',
  guitar: 'text-mf-yellow',
  line: 'border-mf-yellow/20',
  eyebrow: 'text-mf-coral',
  button: 'coral',
  outline: 'outlineLight',
} as const;

/** Builds a combination on a light base: black ink, coralInk accent, green hairlines. */
function light(bg: string, card: string, img: string, overrides: Partial<ThemeClasses> = {}): ThemeClasses {
  return {
    ...LIGHT,
    bg,
    img,
    card: { bg: card, text: LIGHT.heading, muted: LIGHT.body, border: LIGHT.line },
    ...overrides,
  };
}

/** Builds a combination on a dark base: yellow ink, coral accent, yellow hairlines. */
function dark(bg: string, card: string, img: string, overrides: Partial<ThemeClasses> = {}): ThemeClasses {
  return {
    ...DARK,
    bg,
    img,
    card: { bg: card, text: DARK.heading, muted: DARK.body, border: DARK.line },
    ...overrides,
  };
}

export const THEME_CLASSES: Record<PageSectionTheme, ThemeClasses> = {
  yellow: light('bg-mf-yellow', 'bg-mf-yellowDeep', 'bg-mf-yellowImg'),
  green:  dark ('bg-mf-green',  'bg-mf-greenLight', 'bg-mf-greenImg'),
  ice:    light('bg-mf-ice',    'bg-mf-iceDeep',    'bg-mf-iceImg'),
  white:  light('bg-white',     'bg-mf-whiteDeep',  'bg-mf-whiteImg'),
  grey:   light('bg-mf-grey',   'bg-mf-greyDeep',   'bg-mf-greyImg'),
  orange: light('bg-mf-orange', 'bg-mf-orangeDeep', 'bg-mf-orangeImg'),
  plum:   dark ('bg-mf-plum',   'bg-mf-plumLight',  'bg-mf-plumImg'),
  // Coral is the exception on both accents: a coral eyebrow would vanish into the background,
  // and a coral CTA with it — so the eyebrow goes plum and the primary pill goes ink/yellow.
  // Editors are advised not to use coral as a section fill at all (see the rollout notes).
  coral:  light('bg-mf-coral',  'bg-mf-coralDeep',  'bg-mf-coralImg', {
    eyebrow: 'text-mf-plum',
    button: 'ink',
  }),
};

export function themeOf(theme: PageSectionTheme): ThemeClasses {
  return THEME_CLASSES[theme] ?? THEME_CLASSES.yellow;
}

/** The shared eyebrow style — small, semibold, uppercase, lightly tracked. Pair it with the
 *  combination's `eyebrow` color. Replaces v1's mono, wide-tracked label. */
export const EYEBROW = 'text-xs font-semibold uppercase tracking-[0.12em]';

/** Child-element styling for sanitized HTML bodies (color comes from caller). */
export const RICHTEXT =
  'font-normal text-base md:text-lg leading-relaxed ' +
  '[&_h2]:font-display [&_h2]:text-3xl [&_h2]:md:text-[40px] [&_h2]:leading-none [&_h2]:tracking-[-0.02em] [&_h2]:mt-10 [&_h2]:mb-5 [&_h2:first-child]:mt-0 ' +
  '[&_h3]:font-display [&_h3]:text-[22px] [&_h3]:md:text-2xl [&_h3]:leading-tight [&_h3]:mt-7 [&_h3]:mb-3 ' +
  '[&_p]:mb-4 ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-2 ' +
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:space-y-2 ' +
  '[&_li]:pl-1 ' +
  '[&_b]:font-semibold [&_strong]:font-semibold ' +
  '[&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-mf-coral ' +
  '[&>*:last-child]:mb-0';

/** Fill + ink for a filled control that is not a PillButton — a badge, an active pager dot,
 *  a bespoke CTA anchor. Keyed by the combination's `button` role so it stays in step with
 *  the pills around it (and never puts coral on coral). */
export const BUTTON_FILL: Record<ThemeClasses['button'], string> = {
  coral: 'bg-mf-coral text-white hover:bg-mf-coralImg',
  ink:   'bg-mf-ink text-mf-yellow hover:bg-mf-ink/90',
};

/** Border + ink for an outline control that is not a PillButton — the gallery/carousel
 *  arrows and the news pager. Keyed by the combination's `outline` role. */
export const OUTLINE_CONTROL: Record<ThemeClasses['outline'], string> = {
  outline:      'border border-mf-green/20 text-mf-black hover:bg-mf-green/5',
  outlineLight: 'border border-mf-yellow/20 text-mf-yellow hover:bg-mf-yellow/10',
};
